import moment from 'moment/moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EmpPosition } from '~/data/employee';
import { EmployeesIndexed } from '~/data/employees';
import { AllEnums } from '~/data/enums';
import { LegalEntity } from '~/data/legalEntity';
import { EmploymentPositionProposalProps } from '~/data/proposals';
import {
  getTranslationKeyForObjectKey,
  scheduledDateFormats,
} from '~/features/ScheduledEmploymentChanges/helpers';
import {
  EmploymentUpcomingChangeDifference,
  UpcomingChangeValueFormatter,
  useResolveUpcomingChanges,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Common/EmploymentUpcomingChanges';
import { empPositionNewTypeToLegacy } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data/newTypeToLegacy';
import { useUpcomingCostCenterChanges } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data/useUpcomingCostCenterChanges';
import { ScheduledEmploymentUnitSelection } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useScheduleEmployment';
import { PendingScheduleChanges } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { filterActiveEnums } from '~/utils/helpers/enum';

import { useUpcomingUnitPositionChanges } from './data/useUpcomingUnitPositionChanges';

enum ObjectKeyTranslation {
  'fEmpLegalEntity' = 'fEmpLegalEntity',
  'fInternalPositionInfo' = 'laInfo',
  'fEmpJobTitle' = 'fEmpJobTitle',
  'fInternalPositionUnit' = 'laUnit',
  'fInternalPositionPosition' = 'laPosition',
  'fInternalPositionManager' = 'laManager',
}

const EmpPositionKeys = [
  'fEmpCostCenters',
  'fEmpJobTitle',
  'fEmpLegalEntity',
  'fEmpUnitPositions',
  'fInternalPositionAttachmentId',
  'fInternalPositionAttachmentName',
  'fInternalPositionEmpId',
  'fInternalPositionEnteredTime',
  'fInternalPositionId',
  'fInternalPositionIncludeInOverallExperience',
  'fInternalPositionInfo',
  'fInternalPositionIsCurrent',
  'fInternalPositionIsPending',
  'fInternalPositionManager',
  'fInternalPositionPosition',
  'fInternalPositionStartDate',
  'fInternalPositionUnit',
];

type PropsType = {
  proposals: EmploymentPositionProposalProps[];
  defaultData: EmpPosition;
  proposalWorkflowsEnabled: boolean;
  pendingScheduleChanges: {
    index: number;
    pendingData?: PendingScheduleChanges[];
  };
  fieldValueFormatProps: {
    legalEntities: LegalEntity[];
    unitSelection: ScheduledEmploymentUnitSelection;
    allEnums: AllEnums;
    allEmployees: EmployeesIndexed;
    managers?: number[];
  };
};

export const useUpcomingScheduledEmployment = (props: PropsType) => {
  const {
    proposals,
    proposalWorkflowsEnabled,
    defaultData,
    pendingScheduleChanges,
    fieldValueFormatProps,
  } = props;

  const { t } = useTranslation();
  const { resolveUpcomingChanges, resolveProposalsChangesSequence } =
    useResolveUpcomingChanges();

  const { resolveUpcomingCostCenterChanges, isLoading: isLoadingCostCenters } =
    useUpcomingCostCenterChanges();

  const {
    resolveUpcomingUnitPositionChanges,
    isLoading: isLoadingUnitPositions,
  } = useUpcomingUnitPositionChanges();

  const attributeExceptions = useMemo(
    () => [
      'fInternalPositionAttachmentId',
      'fInternalPositionAttachmentName',
      'fInternalPositionEnteredTime',
      'fInternalPositionId',
      'fInternalPositionIncludeInOverallExperience',
      'fInternalPositionIsCurrent',
      'fInternalPositionIsPending',
      'fInternalPositionStartDate',
      'fInternalPositionEmpId',
      'fEmpCostCenters',
      'fEmpUnitPositions',
    ],
    [],
  );

  const translateFieldName = useCallback(
    (key: string): string => {
      const translationKey = getTranslationKeyForObjectKey<
        typeof ObjectKeyTranslation
      >(key, ObjectKeyTranslation);

      return t(translationKey);
    },
    [t],
  );

  const formatFieldValue: UpcomingChangeValueFormatter = useCallback(
    (id: string, value?: string | number | boolean): string => {
      const { legalEntities, unitSelection, allEnums, allEmployees, managers } =
        fieldValueFormatProps;
      if (typeof value === 'undefined') {
        return '';
      }

      switch (id) {
        case 'fEmpLegalEntity':
          return (
            legalEntities.find((le) => le.fLegalEntityId === value)
              ?.fLegalEntityName || ''
          );
        case 'fInternalPositionInfo':
        case 'fEmpJobTitle':
          return `${value}`;
        case 'fInternalPositionUnit':
          const unitOptions = unitSelection.options;
          return (
            (unitOptions.allUnitOptionsEnabled
              ? unitOptions.allUnits
              : unitOptions.myUnits
            ).find((units) => units.fOrgUnitId === value)?.fOrgUnitName || ''
          );
        case 'fInternalPositionPosition':
          return (
            filterActiveEnums(allEnums['EMPLOYEE_POSITION']).find(
              (ep) => ep?.code === value,
            )?.name || ''
          );
        case 'fInternalPositionManager':
          const manager = managers?.find((mg) => mg === value) || null;
          return manager
            ? `${allEmployees[manager]?.fEmpLastName || ''}, ${
                allEmployees[manager]?.fEmpFirstName || ''
              }`
            : '';
        default: {
          return `${value}`;
        }
      }
    },
    [fieldValueFormatProps],
  );

  const resolveUpcoming = useCallback(
    (
      oldData: EmpPosition,
      newData: EmpPosition,
    ): EmploymentUpcomingChangeDifference[] => {
      if (!oldData) {
        // if this is a new manager then no oldData is sent
        return [];
      }
      const oldCostCenters = oldData.fEmpCostCenters.map((cc) => ({
        ...cc,
        fEmpCostCenterPercent: cc.fEmpCostCenterPercent ?? 0,
      }));

      const costCenterDiffs = resolveUpcomingCostCenterChanges(
        oldCostCenters,
        newData.fEmpCostCenters,
      );

      const oldUnitPositions = oldData.fEmpUnitPositions.map((up) => ({
        ...up,
        fEmpUnitPosPercent: up.fEmpUnitPosPercent ?? 0,
      }));

      const unitPositionDiffs = resolveUpcomingUnitPositionChanges(
        oldUnitPositions,
        newData.fEmpUnitPositions,
      );

      const mainDiffs = resolveUpcomingChanges({
        standardFields: {
          fieldKeys: EmpPositionKeys,
          fieldKeyExceptions: attributeExceptions,
          oldData,
          newData,
          labelTranslator: translateFieldName,
          valueFormatter: formatFieldValue,
        },
      });

      return [...mainDiffs, ...costCenterDiffs, ...unitPositionDiffs];
    },
    [
      resolveUpcomingChanges,
      attributeExceptions,
      translateFieldName,
      formatFieldValue,
      resolveUpcomingCostCenterChanges,
      resolveUpcomingUnitPositionChanges,
    ],
  );

  const upcomingChanges = useMemo(() => {
    const { index, pendingData } = pendingScheduleChanges;
    const newArray = pendingData?.[index]?.empPosition;

    if (!!newArray && defaultData) {
      const previousEmpPosition = pendingData?.[index - 1]?.empPosition;
      const oldArray =
        index === 0 || !previousEmpPosition ? defaultData : previousEmpPosition;

      return resolveUpcoming(oldArray, newArray);
    }
    return [];
  }, [defaultData, pendingScheduleChanges, resolveUpcoming]);

  const proposalChanges = useMemo(() => {
    if (!proposalWorkflowsEnabled || proposals.length < 1) {
      return {
        upcoming: [],
        onGoing: undefined,
      };
    }

    const labelResolver = (prop: EmploymentPositionProposalProps) =>
      moment(prop.proposalData.ip.startDate).format(
        scheduledDateFormats.dotFormat,
      );

    return resolveProposalsChangesSequence({
      proposals,
      proposalToLegacyProps: empPositionNewTypeToLegacy,
      defaultData,
      labelResolver,
      valueChangesResolver: resolveUpcoming,
    });
  }, [
    proposalWorkflowsEnabled,
    proposals,
    defaultData,
    resolveUpcoming,
    resolveProposalsChangesSequence,
  ]);

  return {
    proposalChanges,
    upcomingChanges,
    isLoading: isLoadingCostCenters || isLoadingUnitPositions,
  };
};
